import React from "react";
import { Phone, MapPin, Mail, User, Briefcase } from "lucide-react";
import { Page } from "@/components/Page";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { RevealStagger, StaggerItem } from "@/components/Reveal";
import { ASSETS, CONTACTS, COMPANY } from "@/data/site";

const Contact = () => {
  const { ceo, operations, office } = CONTACTS;
  return (
    <Page>
      <SEO title="Contact" description="Contact Speed Shipping Agencies — Head Office at Nicco House, Kolkata. Reach our executive and operations teams directly." />

      <PageHero
        eyebrow="Contact"
        title="Speak directly with our team"
        intro="Reach the people who run our operations. For enquiries, our executive and operations contacts are available below."
        watermark
      />

      <section data-testid="contact-info-section" className="relative py-24 lg:py-32 overflow-hidden">
        <img
          src={ASSETS.emblem}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -left-20 bottom-0 w-[520px] max-w-[60%] opacity-[0.04] select-none"
        />
        <div className="ssa-container relative">
          <RevealStagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* CEO */}
            <StaggerItem>
              <div data-testid="contact-info-card-ceo" className="h-full rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-turquoise">
                  <User className="h-6 w-6" />
                </div>
                <div className="mt-6 eyebrow text-gold">{ceo.role}</div>
                <h3 className="mt-2 text-xl text-navy">{ceo.name}</h3>
                <a href={`tel:${ceo.phone.replace(/\s/g, "")}`} className="mt-4 inline-flex items-center gap-2.5 text-sm text-secondary-ssa hover:text-royal transition-colors">
                  <Phone className="h-4 w-4 text-turquoise" /> {ceo.phone}
                </a>
              </div>
            </StaggerItem>

            {/* Operations */}
            <StaggerItem>
              <div data-testid="contact-info-card-operations" className="h-full rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-turquoise">
                  <Briefcase className="h-6 w-6" />
                </div>
                <div className="mt-6 eyebrow text-gold">{operations.role}</div>
                <h3 className="mt-2 text-xl text-navy">{operations.name}</h3>
                <a href={`tel:${operations.phone.replace(/\s/g, "")}`} className="mt-4 inline-flex items-center gap-2.5 text-sm text-secondary-ssa hover:text-royal transition-colors">
                  <Phone className="h-4 w-4 text-turquoise" /> {operations.phone}
                </a>
              </div>
            </StaggerItem>

            {/* Head Office */}
            <StaggerItem>
              <div data-testid="contact-info-card-head-office" className="h-full rounded-2xl bg-navy text-white p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-turquoise">
                  <MapPin className="h-6 w-6" />
                </div>
                <div className="mt-6 eyebrow text-turquoise">{office.role}</div>
                <div className="mt-3 text-sm leading-relaxed text-white/85">
                  {office.lines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-white/10 space-y-3">
                  <a
                    href={`tel:${COMPANY.officePhoneTel}`}
                    className="flex items-center gap-2.5 text-sm text-white/85 hover:text-turquoise transition-colors"
                  >
                    <Phone className="h-4 w-4 text-turquoise" /> {COMPANY.officePhone}
                  </a>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="flex items-center gap-2.5 text-sm text-white/85 hover:text-turquoise transition-colors break-all"
                  >
                    <Mail className="h-4 w-4 text-turquoise" /> {COMPANY.email}
                  </a>
                </div>
              </div>
            </StaggerItem>
          </RevealStagger>

          <div className="mt-10 rounded-2xl ocean-sheen border border-[color:var(--ssa-border-subtle)] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="text-xl text-navy">{COMPANY.name}</h3>
              <p className="mt-1 text-sm text-secondary-ssa">{COMPANY.tagline}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <a
                href={`mailto:${COMPANY.email}`}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-[color:var(--ssa-border-strong)] bg-white text-navy text-sm font-medium hover:bg-white/80 transition-colors"
              >
                Get In Touch <Mail className="h-4 w-4" />
              </a>
              <a
                href={`tel:${COMPANY.officePhoneTel}`}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-navy text-white text-sm font-medium hover:bg-[#0B254B] transition-colors"
              >
                Call our office <Phone className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default Contact;
